import { SelectIdGroupContext } from '_context/SelectGroupContext'
import {
  deleteMultipleProductsAction,
  getListProductsByHost,
  searchProductsByHostAction,
} from '_redux/modules/product'
import { useCallback, useContext, useEffect, useState, useMemo } from 'react'
import { FaPlus, FaTags } from 'react-icons/fa'
import { FiTrash2 } from 'react-icons/fi'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useToasts } from 'react-toast-notifications'
import { Col, Container, Row, Spinner } from 'reactstrap'
import { SelectProductContext } from '../../context/ProductContext'
import useDebounce from '../../hooks/useDebounce'
import useLoadingState from '../../hooks/useLoadingState'
import { useModal } from '../../hooks/useModal'
import {
  selectProductsFlatten,
  selectSearchResultFlatten,
} from '../../redux/selectors/productSelectors'
import ProductPagination from './components/ProductPagination'
import SearchBar from '../components/SearchBar'
import ProductTable from './components/ProductTable'
import ProductFilter from './components/ProductFilter'
import CategoryModal from './components/CategoryModal'
import ModalAssignProduct from './components/ModalProduct/ModalAssignProduct'
import './style.scss'
import { getCreateEventData, setCreateEventData } from '../../utils/localData'
import { MAX_SELECT_PRODUCT, PRODUCTS_PAGE_SIZE } from '../../utils/constant'
import ButtonCommon from '../components/Button'

export default function ProductsItems() {
  const history = useHistory()
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const [searchString, setSearchString] = useState('')
  const debouncedSearch = useDebounce(searchString, 500)
  const { isLoading, withLoading, startLoading, stopLoading } = useLoadingState()
  const { isOpen: isAssignModalOpen, toggleModal: toggleAssignModal } = useModal()

  const context = useContext(SelectIdGroupContext)
  const { setSelectedProduct } = useContext(SelectProductContext)

  const [selectedItems, setSelectedItems] = useState([])
  const { isOpen: isCategoryModalOpen, toggleModal: toggleCategoryModal } = useModal()

  const handleToggleSelectRow = useCallback(
    (item) => {
      const isSelected = selectedItems.some((i) => i.id === item.id)
      if (!isSelected && selectedItems.length >= 10) {
        addToast('You can only select up to 10 products at a time', {
          appearance: 'warning',
          autoDismiss: true,
        })
        return
      }

      setSelectedItems((prev) => {
        if (isSelected) {
          return prev.filter((i) => i.id !== item.id)
        }
        return [...prev, item]
      })
    },
    [selectedItems, addToast]
  )

  const handleToggleSelectAll = useCallback(
    (items) => {
      const unselectedItems = items.filter(
        (i) => !selectedItems.some((s) => s.id === i.id)
      )
      const canAddMore = unselectedItems.length > 0 && selectedItems.length < MAX_SELECT_PRODUCT
      const isAllSelected = !canAddMore && selectedItems.some((s) =>
        items.some((i) => i.id === s.id))

      if (isAllSelected) {
        setSelectedItems((prev) =>
          prev.filter((s) => !items.some((i) => i.id === s.id)))
      } else {
        setSelectedItems((prev) => {
          const newSelected = [...prev]
          for (const item of unselectedItems) {
            if (newSelected.length >= MAX_SELECT_PRODUCT) break
            newSelected.push(item)
          }
          return newSelected
        })
      }
    },
    [selectedItems]
  )
  const handleConfirmDelete = async () => {
    if (selectedItems.length === 0) return

    await withLoading('delete', async () => {
      try {
        const data = {
          products: selectedItems.map((item) => ({
            productId: item.id,
            eventId: item.eventId,
          })),
        }
        const { msgResp } = await dispatch(deleteMultipleProductsAction(data))
        addToast(msgResp?.message, {
          appearance: 'success',
          autoDismiss: true,
        })

        setSelectedItems([])
      } catch (error) {
        addToast(error?.msgResp || 'Delete failed', {
          appearance: 'error',
          autoDismiss: true,
        })
      }
    })()
  }

  const handleCreateEvent = () => {
    const createEventData = getCreateEventData()

    if (selectedItems?.length > 0 || createEventData?.products?.length > 0) {
      const mappedSelectedItems = (selectedItems || []).map((item) => ({
        pid: '',
        name: item.name || '',
        description: item.description || null,
        price: item.price ?? 0,
        maxQuantity: item.maxQuantity ?? 1000,
        defaultQuantity: item.defaultQuantity ?? 0,
        limitPerOrder: item.limitPerOrder ?? 1000,
        subItems: item.subItems || [],
        photoUrls: item.photoUrls || [],
        splitCasePurchase: item.splitCasePurchase || null,
        minOrderQty: item.minOrderQty ?? 1,
      }))

      setCreateEventData({
        products: [
          ...(createEventData?.products || []),
          ...mappedSelectedItems
        ]
      })
    }
    history.push('/event/hosting')
  }

  const { groupId } = context.infoGroupSelected
  const rawProducts = useSelector(selectProductsFlatten)
  const searchResult = useSelector(selectSearchResultFlatten)
  const events = useSelector((state) => state.product.listProduct?.events || [])

  const [eventFilter, setEventFilter] = useState([])
  const [statusFilter, setStatusFilter] = useState([])
  const [sortFilter, setSortFilter] = useState(null)
  const [categoryFilter, setCategoryFilter] = useState([])

  const keyword = debouncedSearch.trim().toLowerCase()

  const matchesKeyword = useCallback(
    (item) =>
      item.name?.toLowerCase().includes(keyword) || item.eventName?.toLowerCase().includes(keyword),
    [keyword]
  )

  const localMatches = useMemo(() => {
    const items = rawProducts.items || []
    return keyword ? items.filter(matchesKeyword) : items
  }, [rawProducts.items, keyword, matchesKeyword])

  // Nothing found in the already-loaded buffer: fall back to a server-side search.
  const isFallbackSearch = keyword !== '' && localMatches.length === 0
  const useFallbackResult = isFallbackSearch && searchResult.keyword === keyword

  useEffect(() => {
    if (!groupId || !isFallbackSearch || useFallbackResult) return

    let isStale = false
    startLoading('search')
    dispatch(searchProductsByHostAction({ groupId, searchString: keyword })).finally(() => {
      if (!isStale) stopLoading('search')
    })

    // The search itself can't be aborted mid-flight, but once the keyword changes
    // (e.g. the user clears the box) its result is no longer relevant: stop showing
    // it as loading right away instead of waiting for that stale request to resolve.
    return () => {
      isStale = true
      stopLoading('search')
    }
  }, [groupId, isFallbackSearch, useFallbackResult, keyword, dispatch])

  const baseSource = useFallbackResult ? searchResult : { ...rawProducts, items: localMatches }

  const products = useMemo(() => {
    let items = baseSource.items || []

    if (categoryFilter && categoryFilter.length > 0) {
      const selectedCategoryIds = categoryFilter.map((category) => category.id || category.value)
      items = items.filter((item) => selectedCategoryIds.includes(item.categoryId))
    }

    if (eventFilter && eventFilter.length > 0) {
      const selectedEventIds = eventFilter.map((e) => e.id)
      items = items.filter((item) => selectedEventIds.includes(item.eventId))
    }

    if (statusFilter && statusFilter.length > 0) {
      const now = Math.floor(Date.now() / 1000)
      const selectedStatuses = statusFilter.map((s) => String(s.value))
      items = items.filter((item) => {
        const isOpen = item.eventClosingTime > now
        if (isOpen && selectedStatuses.includes('1')) return true
        if (!isOpen && selectedStatuses.includes('0')) return true
        return false
      })
    }

    if (sortFilter) {
      items = items.slice().sort((a, b) => {
        switch (sortFilter.value) {
          case 'PRICE_ASC':
            return (a.price || 0) - (b.price || 0)
          case 'PRICE_DESC':
            return (b.price || 0) - (a.price || 0)
          case 'TOTAL_PURCHASE_ASC':
            return (a.orderCount || 0) - (b.orderCount || 0)
          case 'TOTAL_PURCHASE_DESC':
            return (b.orderCount || 0) - (a.orderCount || 0)
          case 'STOCK_ASC':
            return (a.inStock || 0) - (b.inStock || 0)
          case 'STOCK_DESC':
            return (b.inStock || 0) - (a.inStock || 0)
          default:
            return 0
        }
      })
    }

    return { ...baseSource, items }
  }, [baseSource, categoryFilter, eventFilter, statusFilter, sortFilter])

  // A local filter (event/status/search/category) shrinks the visible count independently of how
  // much raw data is buffered, so `hasMore` from the raw buffer can't be trusted to decide
  // whether fetching another chunk will ever produce a full page — fetching more in that
  // case can spiral into pulling the whole product history just to satisfy one page size.
  // The dedicated search fallback above already covers "look further for a match"; outside
  // of that, local filtering only searches within what's already loaded.
  const isLocalFilterActive =
    keyword !== '' ||
    eventFilter.length > 0 ||
    statusFilter.length > 0 ||
    (categoryFilter && categoryFilter.length > 0)
  const allowBufferExtension = useFallbackResult || !isLocalFilterActive

  const handleClearFilter = useCallback(() => {
    setEventFilter([])
    setStatusFilter([])
    setSortFilter(null)
    setCategoryFilter([])
  }, [])

  useEffect(() => {
    if (!groupId) return
    dispatch(getListProductsByHost({ groupId }))
  }, [groupId, dispatch])

  const handleSearchChange = useCallback((value) => {
    setSearchString(value)
  }, [])

  const [pageRange, setPageRange] = useState({ start: 0, end: PRODUCTS_PAGE_SIZE })
  const handlePageChange = useCallback((range) => {
    setPageRange(range)
  }, [])

  const pagedItems = useMemo(
    () => products.items.slice(pageRange.start, pageRange.end),
    [products.items, pageRange]
  )

  return (
    <Container id='products-items' fluid className='py-2'>
      {/* Title */}
      <Row className='mb-3 mt-2'>
        <Col md={4}>
          <span className='page-title'>Products & Items</span>
          <p className='page-subtitle'>Manage your product catalogue across all events</p>
        </Col>
        <Col md={8} className='d-flex gap-2 justify-content-end align-items-center'>
          {selectedItems?.length > 0 ? (
            <>
              <ButtonCommon
                variant='outline'
                content={`Assign Product (${selectedItems.length})`}
                onClick={toggleAssignModal}
                icon={<FaTags />}
              />
              <ButtonCommon
                variant='outline-danger'
                content={`Delete (${selectedItems.length})`}
                disabled={isLoading('delete')}
                onClick={handleConfirmDelete}
                icon={
                  isLoading('delete') ? (
                    <Spinner size='sm' style={{ marginRight: '4px' }} />
                  ) : (
                    <FiTrash2 />
                  )
                }
              />
            </>
          ) : (
            <ButtonCommon
              variant='outline'
              content='Create Category'
              icon={<FaPlus />}
              onClick={toggleCategoryModal}
            />
          )}
          <ButtonCommon
            variant='outline'
            content='Export'
            icon={<svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><path d='M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4'></path><polyline points='7 10 12 15 17 10'></polyline><line x1='12' y1='15' x2='12' y2='3'></line></svg>}
          />
          <ButtonCommon
            variant='outline'
            content='Create Event'
            onClick={handleCreateEvent}
            icon={<svg width='13' height='13' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><rect x='3' y='4' width='18' height='18' rx='2'></rect><line x1='16' y1='2' x2='16' y2='6'></line><line x1='8' y1='2' x2='8' y2='6'></line><line x1='3' y1='10' x2='21' y2='10'></line><line x1='12' y1='14' x2='12' y2='18'></line><line x1='10' y1='16' x2='14' y2='16'></line></svg>}
          />
          <Link to='/products-items/add'>
            <ButtonCommon
              variant='primary'
              content='Add Product'
              icon={<FaPlus />}
              onClick={() => setSelectedProduct(null)}
            />
          </Link>
        </Col>
      </Row>
      <div>
        <SearchBar
          value={searchString}
          onChange={handleSearchChange}
          loading={isLoading('search')}
          placeholder='Search name / event of product'
          debounceTime={0}
        />
        <ProductFilter
          events={events}
          eventFilter={eventFilter}
          statusFilter={statusFilter}
          sortFilter={sortFilter}
          categoryFilter={categoryFilter}
          onChangeEvent={setEventFilter}
          onChangeEventStatus={setStatusFilter}
          onChangeSort={setSortFilter}
          onChangeCategory={setCategoryFilter}
          handleClearFilter={handleClearFilter}
        />
        {/* Table */}
        <ProductTable
          items={pagedItems}
          selectedItems={selectedItems}
          onToggleSelect={handleToggleSelectRow}
          onToggleSelectAll={handleToggleSelectAll}
        />
        {/* Pagination */}
        <ProductPagination
          groupId={groupId}
          searchKeyword={useFallbackResult ? keyword : null}
          filterKey={`${eventFilter.map((e) => e.id).join(',')}|${statusFilter
            .map((s) => s.value)
            .join(',')}|${sortFilter?.value || ''}|${(categoryFilter || [])
            .map((c) => c.value)
            .join(',')}|${keyword}`}
          itemsLength={products.items.length}
          lastProductTimestamp={products.lastProductTimestamp}
          lastProductId={products.lastProductId}
          hasMore={allowBufferExtension ? products.hasMore : false}
          onPageChange={handlePageChange}
        />
      </div>
      <CategoryModal isOpen={isCategoryModalOpen} toggle={toggleCategoryModal} />

      <ModalAssignProduct
        isOpen={isAssignModalOpen}
        toggle={toggleAssignModal}
        selectedItems={selectedItems}
        onSuccess={() => setSelectedItems([])}
      />
    </Container>
  )
}
